import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';

import { useIsSignedIn, useOauthConnect } from '@front/auth/src/authentication-context';

import { BasketState } from '@front/common/src/ducks/common/basket';
import { isChildcareRecurring as isChildcareRecurringUtil } from '@front/common/src/utils/basket';
import { useVariant } from '@front/common/src/providers/experiment-provider';

import { getScreenTitle } from '@front/customer/src/screens/signup-choice/utils';

import { translate } from '@front/i18n';

import SocialButton from '@front/web-ui/src/components/button/social-button';
import Separator from '@front/web-ui/src/components/separator/separator';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import Button from '@front/web-ui/src/components/button/button';
import Alert from '@front/web-ui/src/components/alert/alert';
import Screen from '@front/web-ui/src/components/screen/screen';

import { RootState } from '../../store';
import useOAuth from '../../hooks/use-oauth';
import { trackSignInEvent } from '../../utils/tracking';
import { isInWebView } from '../../utils/webview';

import { Container } from './common/styles';
import AlreadySubscribed from './already-subscribed';
import useSignupNavigation from './use-signup-navigation';

const t = translate('screens.signup');

const TextContainer = styled.div`
  margin-bottom: 20px;
`;

const SocialButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const StyledAlert = styled(Alert)`
  width: 100%;
  margin-bottom: 20px;
`;

const StyledSeparator = styled(Separator)`
  margin: 20px 0;
`;

type SignupChoiceProps = {
  universe: BasketState['universe'];
  isChildcareRecurring: boolean;
};

export const SignupChoice = ({ universe, isChildcareRecurring }: SignupChoiceProps) => {
  const [googleSignInError, setGoogleSignInError] = useState<string>();
  const location = useLocation();
  const isSignedIn = useIsSignedIn();
  const { onAccountCreated, openLoginScreen, gotoSignup } = useSignupNavigation(isSignedIn);
  const { oauthConnectError, oauthConnect } = useOauthConnect({
    onSuccess: onAccountCreated,
    onPartialConnect: gotoSignup,
  });

  const variantForTitle = useVariant('cst-cleaning-fr-signup-new-title');

  const isInsideWebView = isInWebView();

  const {
    facebookLogin,
    facebookInitialized,
    error: thirdPartyError,
    thirdPartyCalled,
    triggerGoogleSignInBackendProcess,
  } = useOAuth(oauthConnect);

  const onEmailButtonClick = useCallback(() => {
    trackSignInEvent({
      eventType: 'EmailButtonClick',
      universe,
      path: location.pathname,
    });
    gotoSignup();
  }, [gotoSignup, universe, location.pathname]);

  const onFacebookButtonClick = useCallback(() => {
    trackSignInEvent({
      eventType: 'FacebookButtonClick',
      universe,
      path: location.pathname,
    });
    facebookLogin();
  }, [facebookLogin, universe, location.pathname]);

  const triggerGoogleSignInProcess = useGoogleLogin({
    scope: 'email profile',
    onSuccess: ({ access_token: accesToken }) => {
      triggerGoogleSignInBackendProcess(accesToken);
    },
    onError: (error) => {
      setGoogleSignInError(error.error_description);
    },
  });

  const onGoogleButtonClick = () => {
    trackSignInEvent({
      eventType: 'GoogleButtonClick',
      universe,
      path: location.pathname,
    });
    triggerGoogleSignInProcess();
  };

  const hasErrors = !!(thirdPartyError || oauthConnectError);

  return (
    <Screen title={getScreenTitle({ isChildcareRecurring, variantForTitle: variantForTitle?.value })}>
      <Container>
        {isChildcareRecurring && (
          <TextContainer>
            <Text>{t('.subtitle_childcare')}</Text>
          </TextContainer>
        )}
        {googleSignInError && <Alert type='error' content={googleSignInError} />}
        {thirdPartyError && <StyledAlert type='error' content={thirdPartyError} />}
        {oauthConnectError && <StyledAlert type='error' content={oauthConnectError} />}
        <Button size='lg' hasFullWidth onPress={onEmailButtonClick}>
          {t('.email')}
        </Button>
        <StyledSeparator text={t('base.or')} />
        <SocialButtonContainer>
          {!isInsideWebView && (
            <SocialButton
              provider='google'
              onPress={onGoogleButtonClick}
              isLoading={thirdPartyCalled === 'google' && !hasErrors}
              authType='signup'
            />
          )}
          <SocialButton
            provider='facebook'
            onPress={onFacebookButtonClick}
            isDisabled={!facebookInitialized}
            isLoading={thirdPartyCalled === 'facebook' && !hasErrors}
            authType='signup'
          />
        </SocialButtonContainer>
        <AlreadySubscribed universe={universe} openLoginScreen={openLoginScreen} />
      </Container>
    </Screen>
  );
};

export const mapStateToProps = (state: RootState) => ({
  universe: state.basket.universe,
  isChildcareRecurring: isChildcareRecurringUtil(state.basket.basketContent),
});

export default connect(mapStateToProps, {})(SignupChoice);
